/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, Check, X, Crown, Loader2, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_OPTIONS = ['basic', 'pro', 'elite'];
const TYPE_OPTIONS = ['catch', 'photo', 'community', 'giveaway', 'league'];
const SELECTION_OPTIONS = ['admin_pick', 'community_vote', 'top_weight', 'top_likes'];

const emptyForm = {
  slug: '',
  title: '',
  description: '',
  category: 'general',
  tier_required: 'basic',
  competition_type: 'catch',
  prize_summary: '',
  prize_value_estimate: '',
  starts_at: '',
  ends_at: '',
  is_active: true,
  is_pinned: false,
  banner_image: '',
  rules: '',
  winner_selection: 'admin_pick',
};

const isoDateTimeLocal = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

const AdminCompetitions = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {id..}=edit
  const [pickingWinnerFor, setPickingWinnerFor] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchComps();
  }, [user, authLoading, navigate]);

  const fetchComps = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/competitions?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComps(r.data);
    } catch (e) {
      toast.error('Failed to load competitions');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (form) => {
    const payload = {
      ...form,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : new Date().toISOString(),
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : new Date(Date.now() + 30 * 86400000).toISOString(),
      prize_value_estimate: form.prize_value_estimate ? Number(form.prize_value_estimate) : null,
    };

    try {
      if (form.id) {
        // Update — only send changed fields
        const { id, slug: _s, ...updatable } = payload;
        await axios.patch(`${API}/admin/competitions/${id}`, updatable, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Competition updated');
      } else {
        await axios.post(`${API}/admin/competitions`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Competition created');
      }
      setEditing(null);
      fetchComps();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (comp) => {
    if (!window.confirm(`Delete "${comp.title}"? This also deletes all submissions and winners.`)) return;
    try {
      await axios.delete(`${API}/admin/competitions/${comp.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Competition deleted');
      fetchComps();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const togglePin = async (comp) => {
    try {
      await axios.patch(`${API}/admin/competitions/${comp.id}`, { is_pinned: !comp.is_pinned }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchComps();
    } catch (e) {
      toast.error('Pin failed');
    }
  };

  const toggleActive = async (comp) => {
    try {
      await axios.patch(`${API}/admin/competitions/${comp.id}`, { is_active: !comp.is_active }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchComps();
    } catch (e) {
      toast.error('Toggle failed');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#c8b273] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8" data-testid="admin-competitions-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="text-[#8a948a] hover:text-white transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <p className="data-label text-[#c8b273] mb-1">Admin</p>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-wider text-white">
                Manage Competitions
              </h1>
              <p className="text-[#8a948a] text-sm">{comps.length} competition{comps.length !== 1 ? 's' : ''} total</p>
            </div>
          </div>
          <Button
            className="btn-gold"
            onClick={() => setEditing({})}
            data-testid="admin-new-comp-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Competition
          </Button>
        </div>

        {/* List */}
        <div className="space-y-3">
          {comps.map((c) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className={`glass-card p-4 flex flex-wrap items-center gap-4 ${
                c.is_pinned ? 'border-[#c8b273]/40' : ''
              } ${!c.is_active ? 'opacity-60' : ''}`}
              data-testid={`admin-comp-row-${c.slug}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">{c.title}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[#c8b273]/15 text-[#c8b273]">
                    {c.tier_required}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[#1f221f] text-[#8a948a]">
                    {c.competition_type}
                  </span>
                  {c.is_pinned && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[#c8b273] text-black flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Pinned
                    </span>
                  )}
                  {!c.is_active && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-red-500/15 text-red-400">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#8a948a] truncate">{c.prize_summary} · ends {new Date(c.ends_at).toLocaleDateString('en-GB')}</p>
              </div>

              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPickingWinnerFor(c)}
                  className="text-[#c8b273] hover:bg-[#c8b273]/10 text-xs"
                  data-testid={`admin-winner-btn-${c.slug}`}
                >
                  <Crown className="w-3.5 h-3.5 mr-1" /> Winner
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => togglePin(c)}
                  className="text-[#8a948a] hover:text-white text-xs"
                  data-testid={`admin-pin-btn-${c.slug}`}
                >
                  {c.is_pinned ? 'Unpin' : 'Pin'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleActive(c)}
                  className="text-[#8a948a] hover:text-white text-xs"
                  data-testid={`admin-toggle-btn-${c.slug}`}
                >
                  {c.is_active ? 'Disable' : 'Enable'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing({ ...c, starts_at: isoDateTimeLocal(c.starts_at), ends_at: isoDateTimeLocal(c.ends_at) })}
                  className="text-white hover:bg-white/10 text-xs"
                  data-testid={`admin-edit-btn-${c.slug}`}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(c)}
                  className="text-red-400 hover:bg-red-500/10 text-xs"
                  data-testid={`admin-delete-btn-${c.slug}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        {comps.length === 0 && (
          <div className="glass-card p-10 text-center">
            <p className="text-[#8a948a] mb-4">No competitions yet — create one to get started.</p>
            <Button className="btn-gold" onClick={() => setEditing({})}>
              <Plus className="w-4 h-4 mr-2" /> Create first competition
            </Button>
          </div>
        )}

        {/* Edit/Create modal */}
        {editing && (
          <CompetitionEditModal
            initial={editing}
            onSave={handleSave}
            onClose={() => setEditing(null)}
          />
        )}

        {/* Pick winner modal */}
        {pickingWinnerFor && (
          <PickWinnerModal
            competition={pickingWinnerFor}
            token={token}
            onClose={() => setPickingWinnerFor(null)}
            onPicked={() => {
              setPickingWinnerFor(null);
              fetchComps();
            }}
          />
        )}
      </div>
    </div>
  );
};


// ==================== EDIT MODAL ====================

const CompetitionEditModal = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState({ ...emptyForm, ...initial });
  const isEdit = !!initial.id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass-card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 border-[#c8b273]/30" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
            {isEdit ? 'Edit Competition' : 'New Competition'}
          </h3>
          <button type="button" onClick={onClose} className="text-[#8a948a] hover:text-white" data-testid="admin-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Slug *" disabled={isEdit} value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="catch-of-the-week" testid="admin-field-slug" />
            <FieldInput label="Title *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="admin-field-title" />
          </div>

          <div>
            <FieldLabel>Description *</FieldLabel>
            <Textarea
              data-testid="admin-field-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="bg-black/30 border-white/10 text-white"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FieldSelect label="Tier" value={form.tier_required} onChange={(v) => setForm({ ...form, tier_required: v })} options={TIER_OPTIONS} testid="admin-field-tier" />
            <FieldSelect label="Type" value={form.competition_type} onChange={(v) => setForm({ ...form, competition_type: v })} options={TYPE_OPTIONS} testid="admin-field-type" />
            <FieldSelect label="Winner Selection" value={form.winner_selection} onChange={(v) => setForm({ ...form, winner_selection: v })} options={SELECTION_OPTIONS} testid="admin-field-selection" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Starts At" type="datetime-local" value={form.starts_at} onChange={(v) => setForm({ ...form, starts_at: v })} testid="admin-field-starts" />
            <FieldInput label="Ends At" type="datetime-local" value={form.ends_at} onChange={(v) => setForm({ ...form, ends_at: v })} testid="admin-field-ends" />
          </div>

          <FieldInput label="Prize Summary" value={form.prize_summary} onChange={(v) => setForm({ ...form, prize_summary: v })} placeholder="£50 bait bundle + badge" testid="admin-field-prize" />

          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Prize £ Value" type="number" step="0.01" value={form.prize_value_estimate} onChange={(v) => setForm({ ...form, prize_value_estimate: v })} testid="admin-field-prize-value" />
            <FieldInput label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="admin-field-category" />
          </div>

          <div>
            <FieldLabel>Rules</FieldLabel>
            <Textarea
              data-testid="admin-field-rules"
              value={form.rules}
              onChange={(e) => setForm({ ...form, rules: e.target.value })}
              rows={2}
              className="bg-black/30 border-white/10 text-white"
            />
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-[#e2e4e2]">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="admin-field-active" />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-[#e2e4e2]">
              <input type="checkbox" checked={form.is_pinned} onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })} data-testid="admin-field-pinned" />
              Pin to top
            </label>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" className="btn-gold flex-1" data-testid="admin-save-btn">
              {isEdit ? 'Save Changes' : 'Create Competition'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} className="text-[#8a948a]">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ==================== WINNER PICKER ====================

const PickWinnerModal = ({ competition, token, onClose, onPicked }) => {
  const [subs, setSubs] = useState(null);
  const [statusFilter, setStatusFilter] = useState('approved');
  const [picking, setPicking] = useState(null);
  const [prize, setPrize] = useState('');
  const [quote, setQuote] = useState('');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/competitions/${competition.id}/submissions?status=${statusFilter}`, { headers })
      .then((r) => setSubs(r.data))
      .catch(() => setSubs([]));
  }, [competition.id, statusFilter, token]);

  const handleApprove = async (subId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/competition-submissions/${subId}/status?new_status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked as ${newStatus}`);
      setStatusFilter(newStatus);
    } catch (e) {
      toast.error('Failed');
    }
  };

  const handleDeleteSubmission = async (subId) => {
    if (!window.confirm('Delete this submission permanently? Use this only for spam or policy violations.')) return;
    try {
      await axios.delete(`${API}/admin/competition-submissions/${subId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Submission deleted');
      // Refetch current filter
      const r = await axios.get(
        `${API}/competitions/${competition.id}/submissions?status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubs(r.data);
      if (picking?.id === subId) setPicking(null);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handlePick = async () => {
    if (!picking) return;
    try {
      await axios.post(
        `${API}/admin/competitions/${competition.id}/winners`,
        {
          submission_id: picking.id,
          prize: prize || competition.prize_summary,
          quote: quote || null,
          featured: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Winner selected — ${picking.username}!`);
      onPicked();
    } catch (e) {
      toast.error('Failed to select winner');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass-card max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 border-[#c8b273]/30" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="data-label text-[#c8b273] mb-1">Pick Winner</p>
            <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white">{competition.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#8a948a] hover:text-white" data-testid="admin-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          {['approved', 'pending', 'rejected', 'all'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-widest ${
                statusFilter === s ? 'bg-[#c8b273] text-black' : 'border border-white/10 text-[#8a948a]'
              }`}
              data-testid={`admin-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        {!subs ? (
          <Loader2 className="w-6 h-6 text-[#c8b273] animate-spin mx-auto my-10" />
        ) : !subs.length ? (
          <p className="text-center text-[#8a948a] py-10">No submissions in this filter</p>
        ) : (
          <div className="space-y-2 mb-4 max-h-80 overflow-y-auto">
            {subs.map((s) => (
              <div
                key={s.id}
                onClick={() => setPicking(s)}
                className={`p-3 rounded border cursor-pointer transition-colors ${
                  picking?.id === s.id ? 'border-[#c8b273] bg-[#c8b273]/10' : 'border-white/10 hover:border-white/20'
                }`}
                data-testid={`admin-sub-${s.id}`}
              >
                <div className="flex items-center gap-3">
                  {s.image_url && <img src={s.image_url} alt="" className="w-14 h-14 rounded object-cover" />}
                  <div className="flex-1">
                    <p className="text-white font-bold">{s.title}</p>
                    <p className="text-xs text-[#8a948a]">@{s.username} · {s.weight ? `${s.weight} lb · ` : ''}{s.vote_count} votes · status: {s.status}</p>
                  </div>
                  {s.status === 'pending' && (
                    <div className="flex gap-1">
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleApprove(s.id, 'approved'); }} className="p-1 text-green-400 hover:bg-green-500/10 rounded">
                        <Check className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleApprove(s.id, 'rejected'); }} className="p-1 text-red-400 hover:bg-red-500/10 rounded">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteSubmission(s.id); }}
                    className="p-1 text-[#6b7d6b] hover:text-red-400 hover:bg-red-500/10 rounded"
                    data-testid={`admin-sub-delete-${s.id}`}
                    title="Delete submission permanently"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {picking && (
          <div className="p-3 rounded bg-[#c8b273]/5 border border-[#c8b273]/20 space-y-3">
            <p className="text-sm text-[#c8b273]">Awarding to <strong>@{picking.username}</strong></p>
            <FieldInput label="Prize (defaults to competition prize)" value={prize} onChange={setPrize} placeholder={competition.prize_summary} testid="admin-winner-prize" />
            <div>
              <FieldLabel>Winner Quote (optional)</FieldLabel>
              <Textarea
                data-testid="admin-winner-quote"
                value={quote}
                onChange={(e) => setQuote(e.target.value)}
                rows={2}
                placeholder="A short quote to feature with the winner card…"
                className="bg-black/30 border-white/10 text-white"
              />
            </div>
            <Button onClick={handlePick} className="btn-gold w-full" data-testid="admin-confirm-winner">
              <Crown className="w-4 h-4 mr-2" /> Confirm Winner
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};


// ==================== TINY FIELD HELPERS ====================

const FieldLabel = ({ children }) => (
  <label className="block text-xs font-bold uppercase tracking-widest text-[#8a948a] mb-1.5">{children}</label>
);

const FieldInput = ({ label, value, onChange, type = 'text', placeholder, disabled, step, testid }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Input
      data-testid={testid}
      type={type}
      step={step}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-black/30 border-white/10 text-white"
    />
  </div>
);

const FieldSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded bg-black/30 border border-white/10 text-white px-3 text-sm"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

export default AdminCompetitions;
